"use client";

import Image from "next/image";
import { Button } from "ui";
import Link from "next/link";
import { ArrowLeft, Home } from "lucide-react";
import { useTranslation } from "@/lib/sajilni-localization";

interface NotFoundProps {
  customMessage?: string;
  customTitle?: string;
  showHomeButton?: boolean;
  showBackButton?: boolean;
  imageSrc?: string;
}

const NotFound = ({
  customMessage,
  customTitle,
  showHomeButton = true,
  showBackButton = true,
  imageSrc = "/404-not-found.svg",
}: NotFoundProps) => {
  const { t } = useTranslation("not-found");

  return (
    <div className="flex h-full min-h-[70vh] flex-col items-center justify-center px-4 py-8">
      <div className="flex w-full max-w-lg flex-col items-center text-center">
        <div className="relative mb-6 h-64 w-full sm:h-72 md:h-80">
          <Image
            src={imageSrc}
            width={800}
            height={600}
            className="h-full w-full object-contain"
            alt="Page not found"
            priority
          />
        </div>

        <h1 className="mb-3 text-2xl font-semibold text-gray-800 sm:text-3xl">
          {customTitle || t("Page Not Found")}
        </h1>

        <p className="mb-8 text-gray-600">
          {customMessage ||
            t("The page you are looking for doesn't exist or has been moved.")}
        </p>

        <div className="mt-4 flex justify-center gap-4">
          {showBackButton && (
            <Button onClick={() => window.history.back()} variant="outlined">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("Go Back")}
            </Button>
          )}

          {showHomeButton && (
            <Button asChild buttonColor="primary">
              <Link href="/">
                <Home className="mr-2 h-4 w-4" />
                {t("Go to Homepage")}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
